import { useEffect, useMemo, useState } from "react";
import { Flame, Gift, Clock, Trophy, Sparkles, Bell, BellRing, RotateCw, Play } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { trackEvent } from "@/lib/audit";
import { loadStreak, nextStreakMilestone, pingStreak } from "@/lib/retention/streaks";
import { getCheckInState, rewardForDay } from "@/lib/retention/dailyCheckIn";
import { claimDailyStreak, getMyStreak } from "@/lib/dailyStreak.functions";
import { getNextMatch, msUntil, formatCountdown, isReminderSet, setReminder } from "@/lib/retention/nextMatch";
import { activatePush, getPermission, recordEngagement } from "@/lib/push/pushPermission";
import { getStoredRecap, reopenStoredRecap } from "@/lib/retention/postMatchRecap";
import { getLatestHalftimeMemory, replayHalftimeMemory } from "@/lib/retention/halftimeMemory";

/**
 * SPRINT V1 — Retention surface composing the 5 daily-retention modules:
 * Daily Streak, Daily Check-In, Next Match Countdown, Post-Match Recap,
 * Halftime Memory. Pure presentation, no engine changes.
 */
export function RetentionSprintV1Hub() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    pingStreak();
    void trackEvent("retention_sprint_hub_viewed", {});
    const id = window.setInterval(() => setTick((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="space-y-3">
      <DailyStreakCard />
      <DailyCheckInCard onClaim={() => setTick((x) => x + 1)} />
      <NextMatchCountdownCard tick={tick} />
      <PostMatchRecapCard />
      <HalftimeMemoryCard />
    </div>
  );
}

function Card({ children, accent }: { children: React.ReactNode; accent?: string }) {
  return (
    <div
      className="rounded-xl border border-border/60 bg-background/40 p-3"
      style={accent ? { boxShadow: `inset 0 0 0 1px ${accent}` } : undefined}
    >
      {children}
    </div>
  );
}

function DailyStreakCard() {
  const s = loadStreak();
  const next = nextStreakMilestone(s);
  useEffect(() => {
    void trackEvent("retention_streak_viewed", { days: s.consecutive_days_active, longest: s.longest_streak });
    if (next && s.consecutive_days_active >= next - 1 && s.consecutive_days_active >= 1) {
      void trackEvent("retention_streak_milestone_celebrated", { milestone: next, current: s.consecutive_days_active });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const heat = Math.min(1, s.consecutive_days_active / 14);
  return (
    <Card accent="oklch(0.78 0.2 50 / 0.4)">
      <div className="flex items-center gap-3">
        <div
          className="grid h-12 w-12 place-items-center rounded-xl bg-[oklch(0.78_0.2_50)]/15"
          style={{ boxShadow: s.consecutive_days_active >= 3 ? `0 0 ${6 + heat * 18}px oklch(0.78 0.2 50 / 0.5)` : undefined }}
        >
          <Flame className="h-6 w-6 text-[oklch(0.78_0.2_50)]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Streak diario</div>
          <div className="text-xl font-black tabular-nums">{s.consecutive_days_active} días 🔥</div>
          <div className="text-[10px] text-muted-foreground">Récord: {s.longest_streak}</div>
        </div>
        {next && (
          <div className="text-right">
            <div className="text-[10px] text-muted-foreground">Próximo hito</div>
            <div className="text-sm font-black">Día {next}</div>
          </div>
        )}
      </div>
    </Card>
  );
}

function DailyCheckInCard({ onClaim }: { onClaim: () => void }) {
  const qc = useQueryClient();
  const getMine = useServerFn(getMyStreak);
  const claim = useServerFn(claimDailyStreak);
  const state = getCheckInState();

  const { data } = useQuery({
    queryKey: ["daily-streak"],
    queryFn: () => getMine(),
    staleTime: 30_000,
  });

  const serverStreak = data?.current_streak ?? 0;
  const last = data?.last_claim_at ? new Date(data.last_claim_at).getTime() : null;
  const claimable = !last || Date.now() - last >= 24 * 3600 * 1000;
  const dayIndex = (((claimable ? serverStreak + 1 : serverStreak || 1) - 1) % 7) + 1 || 1;
  const reward = rewardForDay(dayIndex);

  useEffect(() => {
    void trackEvent("daily_checkin_shown", { day_index: dayIndex, reward, claimable });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutation = useMutation({
    mutationFn: () => claim(),
    onSuccess: (res) => {
      if (!res.ok) {
        toast.info("Ya reclamaste hoy. Vuelve mañana 🔥");
        return;
      }
      void trackEvent("daily_checkin_claimed", {
        day_index: res.day_index,
        reward: res.reward_xp,
        source: "server",
        current_streak: res.current_streak,
      });
      toast.success(`+${res.reward_xp} XP · Día ${res.day_index}`, {
        description: `🔥 Racha actual: ${res.current_streak} días`,
      });
      if (res.badge_unlocked) {
        toast.success("🏆 Badge desbloqueado", { description: "WORLD CUP COMMITTED FAN" });
      }
      void qc.invalidateQueries({ queryKey: ["daily-streak"] });
      void qc.invalidateQueries({ queryKey: ["top-streaks"] });
      onClaim();
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "No se pudo reclamar");
    },
  });

  const onClick = () => {
    if (!claimable || mutation.isPending) return;
    mutation.mutate();
  };

  return (
    <Card>
      <div className="mb-2 flex items-center gap-2">
        <Gift className="h-4 w-4 text-primary" />
        <div className="text-xs font-bold">Check-in diario</div>
        <span className="ml-auto text-[10px] text-muted-foreground">Total reclamado: {state.total_claims}</span>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {[1, 2, 3, 4, 5, 6, 7].map((d) => {
          const isCurrent = d === dayIndex;
          const claimed = d < dayIndex || (d === dayIndex && !claimable);
          return (
            <div
              key={d}
              className={
                "flex flex-col items-center rounded-md border px-1 py-1.5 text-[9px] " +
                (claimed
                  ? "border-[oklch(0.78_0.18_145)]/40 bg-[oklch(0.78_0.18_145)]/15 text-foreground"
                  : isCurrent
                    ? "border-[oklch(0.78_0.2_50)]/60 bg-[oklch(0.78_0.2_50)]/15 text-foreground"
                    : "border-border/40 bg-background/30 text-muted-foreground")
              }
            >
              <div className="font-black">D{d}</div>
              <div className="tabular-nums">+{rewardForDay(d)}</div>
            </div>
          );
        })}
      </div>
      <button
        onClick={onClick}
        disabled={!claimable || mutation.isPending}
        className={
          "mt-2 w-full rounded-md px-3 py-2 text-xs font-black transition " +
          (claimable
            ? "bg-primary text-primary-foreground hover:opacity-90"
            : "cursor-not-allowed bg-muted text-muted-foreground")
        }
      >
        {mutation.isPending ? "Reclamando..." : claimable ? `Reclamar +${reward} pts` : "Reclamado hoy ✓"}
      </button>
    </Card>
  );
}

function NextMatchCountdownCard({ tick }: { tick: number }) {
  const info = useMemo(() => getNextMatch(), []);
  const [reminded, setReminded] = useState<boolean>(info ? isReminderSet(info.match_id) : false);
  useEffect(() => {
    if (info) void trackEvent("next_match_countdown_viewed", { match_id: info.match_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!info) return null;
  const ms = msUntil(info.kickoff_iso);
  const { label } = formatCountdown(ms);
  const live = ms <= 0;

  const onRemind = () => {
    setReminder(info.match_id);
    setReminded(true);
    // PR-DISCOVERY-01 · P1-4 — wire to the real push pipeline. Records an
    // engagement signal so SmartPushPrompt can surface later if needed, and
    // requests permission immediately when still `default` so the reminder
    // can actually deliver.
    try {
      recordEngagement("match_join");
      const perm = getPermission();
      if (perm === "default") {
        void activatePush();
        void trackEvent("next_match_reminder_push_requested", { match_id: info.match_id });
      }
    } catch { /* noop */ }
  };
  void tick; // re-render dependency

  return (
    <Card accent="oklch(0.7 0.18 250 / 0.4)">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-[oklch(0.7_0.18_250)]" />
        <div className="text-xs font-bold">Próximo partido en</div>
        {info.competition && (
          <span className="ml-auto rounded-full bg-accent/40 px-2 py-0.5 text-[10px] font-bold uppercase">
            {info.competition}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-sm font-black">{info.home_label} vs {info.away_label}</div>
        <div className="font-mono text-lg font-black tabular-nums">{live ? "EN VIVO" : label}</div>
      </div>
      {!live && (
        <button
          onClick={onRemind}
          disabled={reminded}
          className={
            "mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md px-3 py-1.5 text-[11px] font-bold " +
            (reminded
              ? "bg-[oklch(0.78_0.18_145)]/20 text-foreground"
              : "bg-accent/40 hover:bg-accent/60")
          }
        >
          {reminded ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
          {reminded ? "Te avisaremos antes del pitazo" : "Recordarme antes"}
        </button>
      )}
    </Card>
  );
}

function PostMatchRecapCard() {
  const r = getStoredRecap();
  useEffect(() => {
    if (r) void trackEvent("post_match_recap_viewed", { match_id: r.summary.match_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!r) return null;
  const { summary } = r;
  return (
    <Card accent="oklch(0.75 0.18 145 / 0.4)">
      <div className="mb-2 flex items-center gap-2">
        <Trophy className="h-4 w-4 text-[oklch(0.75_0.18_145)]" />
        <div className="text-xs font-bold">Tu último partido</div>
        <span className="ml-auto text-[10px] text-muted-foreground">+{summary.points_earned} pts</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <Stat label="Predicciones" value={summary.predictions_correct} />
        <Stat label="Encuestas" value={summary.surveys_answered} />
        <Stat label="Ranking" value={`#${summary.ranking_position}`} />
      </div>
      <button
        onClick={() => reopenStoredRecap()}
        className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md bg-accent/40 px-3 py-1.5 text-[11px] font-bold hover:bg-accent/60"
      >
        <RotateCw className="h-3.5 w-3.5" /> Ver resumen completo
      </button>
    </Card>
  );
}

function HalftimeMemoryCard() {
  const mem = getLatestHalftimeMemory();
  useEffect(() => {
    if (mem) void trackEvent("halftime_memory_viewed", { match_id: mem.match_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!mem) return null;
  return (
    <Card accent="oklch(0.7 0.18 320 / 0.4)">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[oklch(0.7_0.18_320)]" />
        <div className="text-xs font-bold">Memoria del entretiempo</div>
        <span className="ml-auto text-[10px] text-muted-foreground">
          {mem.home_label} {mem.home_score}–{mem.away_score} {mem.away_label}
        </span>
      </div>
      <ul className="space-y-1 text-[11px]">
        {mem.top_moments.slice(0, 3).map((m, i) => (
          <li key={i} className="text-muted-foreground">• {m}</li>
        ))}
      </ul>
      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Tus jugadas: {mem.user_actions.predictions} pred · {mem.user_actions.votes} votos · {mem.user_actions.shares} compartidos</span>
      </div>
      <button
        onClick={() => replayHalftimeMemory(mem.match_id)}
        className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md bg-accent/40 px-3 py-1.5 text-[11px] font-bold hover:bg-accent/60"
      >
        <Play className="h-3.5 w-3.5" /> Revivir entretiempo
      </button>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border/40 bg-background/30 px-2 py-1.5 text-center">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-black tabular-nums">{value}</div>
    </div>
  );
}